import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'

interface HomeChartProps {
  data: {
    FIXED: number
    NOT_FIXED: number
    READY_TO_REVALIDATE: number
    OUT_OF_SCOPE: number
  }
}

const HomeChart = ({ data: apiData }: HomeChartProps) => {
  const COLORS = {
    Fixed: '#22c55e',
    'Not Fixed': '#ef4444',
    'Ready to Revalidate': '#f59e0b',
    'Out of Scope': '#6b7280'
  }

  const CustomTooltip = ({ active, payload }: { active?: boolean, payload?: any[] }) => {
    if (!active || !payload || payload.length === 0) return null
    const item = payload[0]
    const name = item?.payload?.status
    const value = item?.value
    const color = COLORS[name as keyof typeof COLORS]
    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
          <span className="font-medium">{name}</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">{value}</span>
        </div>
      </div>
    )
  }

  // Map API data to chart format
  const data = [
    { status: 'Fixed', count: apiData.FIXED },
    { status: 'Not Fixed', count: apiData.NOT_FIXED },
    { status: 'Ready to Revalidate', count: apiData.READY_TO_REVALIDATE },
    { status: 'Out of Scope', count: apiData.OUT_OF_SCOPE }
  ]

  const total = data.reduce((sum, item) => sum + item.count, 0)

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Vulnerability Status</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Total: {total} vulnerabilities
        </p>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              type="number"
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              type="category"
              dataKey="status"
              stroke="#6B7280"
              fontSize={12}
              width={150}
            />
            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={30}>
              {data.map((entry) => (
                <Cell key={`cell-${entry.status}`} fill={COLORS[entry.status as keyof typeof COLORS]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        {data.map((item) => (
          <div key={item.status} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[item.status as keyof typeof COLORS] }}></div>
              <span className="text-sm text-gray-700 dark:text-gray-300">{item.status}</span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default HomeChart